import { type OpenAPIV3 } from 'openapi-types';

export const AppLayoutDefinition: OpenAPIV3.NonArraySchemaObject = {
  type: 'object',
  description: 'The layout definition allows you to tweak several layout elements of the app.',
  additionalProperties: false,
  minProperties: 1,
  properties: {
    login: {
      enum: ['navbar', 'navigation', 'hidden'],
      default: 'navbar',
      description: 'The location of the login button.',
    },
    settings: {
      enum: ['navbar', 'navigation', 'hidden'],
      default: 'navbar',
      description: `The location of the settings button.

If set to \`navigation\`, it will only be visible if \`login\` is also visible in \`navigation\`.
`,
    },
    feedback: {
      enum: ['navbar', 'navigation', 'hidden'],
      default: 'navigation',
      description: `The location of the feedback button.

If set to \`navigation\`, it will only be visible if \`login\` is also visible in \`navigation\`.
`,
    },
    enabledSettings: {
      type: 'array',
      items: {
        enum: ['email', 'name', 'phoneNumber', 'picture', 'languages', 'password'],
      },
      description: 'A list of the settings to display on the settings page.',
    },
    install: {
      enum: ['navbar', 'navigation', 'hidden'],
      default: 'navigation',
      description: `The location of the install button.

If set to \`navigation\`, it will only be visible if \`login\` is also visible in \`navigation\`.
`,
    },
    debug: {
      enum: ['navbar', 'navigation', 'hidden'],
      default: 'hidden',
      description: `The location of the debug button.

If set to \`navigation\`, it will only be visible if \`login\` is also visible in \`navigation\`.
`,
    },
    navigation: {
      enum: ['bottom', 'left-menu', 'hidden', 'top'],
      default: 'left-menu',
      description: `The navigation type to use.

If this is omitted, a collapsable side navigation menu will be rendered on the left.
Top navigation is rendered in the title bar. When the title bar is hidden, top navigation is
rendered as side navigation.
`,
    },
    logo: {
      type: 'object',
      description: 'The settings of the app logo.',
      additionalProperties: false,
      properties: {
        position: {
          enum: ['navbar', 'hidden'],
          default: 'hidden',
          description: 'The location of the app logo.',
        },
        asset: { type: 'string' },
      },
    },
    headerTag: {
      type: 'object',
      description: 'Header text to be shown in a tag next to the page name',
      additionalProperties: false,
      properties: {
        text: {
          description: 'Text to be shown inside the tag',
          $ref: '#/components/schemas/RemapperDefinition',
        },
        hide: {
          description: 'Whether to hide the tag',
          $ref: '#/components/schemas/RemapperDefinition',
        },
      },
    },
    titleBarText: {
      description:
        'Whether to display app name or the page name in the title bar, displays pageName by default',
      type: 'string',
      enum: ['appName', 'pageName'],
    },
    breadcrumbs: {
      type: 'boolean',
      default: false,
      description: `Whether to display a breadcrumb trail below the title bar.

The trail is built from the \`parent\` of each page. A grid layout that names a \`breadcrumbs\`
template area renders the trail in that area instead.
`,
    },
    hideTitleBar: {
      description: 'Whether to hide the title bar',
      type: 'boolean',
      default: false,
    },
    stackedHeader: {
      description: `Whether to render the app logo on its own centered row above the navigation.

Only applies when \`navigation\` is set to \`top\`.
`,
      type: 'boolean',
      default: false,
    },
    navbar: {
      $ref: '#/components/schemas/NavbarLayoutDefinition',
      description: `Responsive grid layout for the top navigation header.

Only applies when \`navigation\` is set to \`top\` and cannot be combined with \`stackedHeader\`.
`,
    },
    builtinPages: {
      $ref: '#/components/schemas/BuiltinPagesLayoutDefinition',
      description: `Responsive grid layout for the built-in pages of the app.

Applies unchanged to every built-in page and every state of it. The available template areas are
\`resend-banner\`, \`title\`, \`content\`, and \`bottom-navigation\`.
`,
    },
    breakpoints: {
      $ref: '#/components/schemas/GridBreakpointsDefinition',
      description: `Minimum viewport widths in pixels at which the tablet and desktop grid layouts apply.

Applies to every responsive grid layout in the app, including pages and the navbar.
`,
    },
    hideGroupDropdown: {
      description: `Whether to hide the group dropdown.

Set to \`true\` to hide it for all users, or provide a list of role names to hide it only
for members holding any of those roles.`,
      oneOf: [{ type: 'boolean' }, { type: 'array', items: { type: 'string' } }],
    },
  },
};
